import { XCircleIcon } from "@heroicons/react/24/solid";
import cn from "classnames";
import DOMPurify from "dompurify";
import { ChangeEvent, KeyboardEvent, ReactNode, useRef, useState } from "react";

interface ListInputProps {
  label: ReactNode;
  items: string[];
  onItemsChange: (items: string[]) => void;
  placeholder?: string;
  maxItems?: number;
  className?: string;
}

const ListInput: React.FC<ListInputProps> = ({
  label,
  items,
  onItemsChange,
  placeholder = "Add items...",
  maxItems = Infinity,
  className = "",
}) => {
  const [inputValue, setInputValue] = useState<string>("");
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Sanitize input to prevent XSS
  const sanitizeInput = (value: string): string => {
    return DOMPurify.sanitize(value, {
      ALLOWED_TAGS: [],
      ALLOWED_ATTR: [],
    });
  };

  const addItem = (item: string) => {
    const trimmedItem = sanitizeInput(item.trim());

    if (!trimmedItem) return;

    // Check if we've reached the max limit
    if (items.length >= maxItems) {
      setError(`You can only add a maximum of ${maxItems} items`);
      return;
    }

    // Check if item already exists
    if (items.includes(trimmedItem)) {
      setError(`"${trimmedItem}" has already been added`);
      return;
    }

    onItemsChange([...items, trimmedItem]);
    setInputValue("");
    setError(null);
  };

  const removeItem = (indexToRemove: number) => {
    onItemsChange(items.filter((_, index) => index !== indexToRemove));
    setError(null);
    inputRef.current?.focus();
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    // Handle comma, tab, enter, and space as delimiters
    if (["Enter", "Tab", " ", ","].includes(e.key)) {
      if (inputValue) {
        e.preventDefault();
        addItem(inputValue);
      }
    }
  };

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;

    // If the last character is a comma, process the item
    if (value.endsWith(",")) {
      addItem(value.slice(0, -1));
    } else {
      setInputValue(value);
      if (error) setError(null);
    }
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const pasteData = e.clipboardData.getData("text");
    const pastedItems = pasteData.split(/[\s,]+/);

    const validItems = pastedItems
      .filter((item) => item.trim())
      .filter((item) => !items.includes(sanitizeInput(item.trim())));

    if (items.length + validItems.length > maxItems) {
      setError(`You can only add a maximum of ${maxItems} items`);
      // Add as many as we can up to the limit
      const availableSlots = maxItems - items.length;
      if (availableSlots > 0) {
        onItemsChange([
          ...items,
          ...validItems.slice(0, availableSlots).map(sanitizeInput),
        ]);
      }
    } else {
      onItemsChange([...items, ...validItems.map(sanitizeInput)]);
    }
  };

  return (
    <div className={`form-control w-full ${className}`}>
      <span className="text-gray-800 dark:text-base-content/80 font-medium text-xs md:text-base mb-2">
        {label}
      </span>
      <div
        className={cn(
          "input input-bordered flex flex-wrap gap-2 items-start py-2 min-h-[3rem] focus-within:outline-none focus-within:border-blue-500 border-gray-300 border-slate-200 dark:border-base-300 rounded-xl bg-base-200",
          error ? "input-error" : "",
          items.length > 0 ? "" : "px-4",
          items.length >= maxItems ? "opacity-60" : "",
          "h-auto overflow-y-auto transition-colors"
        )}
      >
        {/* Item Pills */}
        {items.map((item, index) => (
          <div
            key={index}
            className="badge badge-primary gap-1 px-3 py-4 bg-blue-50 dark:bg-blue-900/40 text-blue-500 dark:text-blue-300 font-semibold border-0 my-1"
          >
            <span>{item}</span>
            <XCircleIcon
              className="w-5 h-5 cursor-pointer text-gray-800 dark:text-base-content/80 mr-[-4px]"
              onClick={() => removeItem(index)}
            />
          </div>
        ))}

        {/* Input */}
        {items.length < maxItems && (
          <input
            ref={inputRef}
            type="text"
            value={inputValue}
            onChange={handleChange}
            onKeyDown={handleKeyDown}
            onPaste={handlePaste}
            placeholder={items.length === 0 ? placeholder : ""}
            className="flex-grow border-none focus:outline-none bg-transparent min-w-[8rem] py-2 text-sm"
            aria-label="List item input"
          />
        )}
      </div>

      {/* Error message */}
      {error && <p className="text-error text-xs mt-1">{error}</p>}

      {/* Counter */}
      {maxItems < Infinity && (
        <div className="text-xs text-base-content/60 mt-1 text-end">
          {items.length} of {maxItems} items
        </div>
      )}
    </div>
  );
};

export default ListInput;
